import React from "react";
import { ClipboardPaste, Copy, X } from "lucide-react";
import { Leg } from "../types/flowchart";
import { ConfiguredSignal } from "../../../../config/signals";
import { baseCard, sectionTitle } from "../../../../theme/styles/classes";
import { TradeConditions, TradeCondition } from "./TradeConditions";
import { OrGroupEditor } from "./OrGroupEditor";
import type { CopiedLegPayload, CopiedSignalPayload } from "./signals/types";
import styles from "./LegCard.module.css";


interface LegCardProps {
  leg: Leg;
  symbolBank: string[];
  onRemoveLeg: (id: string) => void;
  // Entry signal handlers
  onAddEntrySignal: (legId: string, groupIdx: number) => void;
  onRemoveEntrySignal: (legId: string, groupIdx: number, signalIdx: number) => void;
  onUpdateEntrySignal: (legId: string, groupIdx: number, signalIdx: number, signal: ConfiguredSignal) => void;
  onAddEntryGroup: (legId: string) => void;
  onRemoveEntryGroup: (legId: string, groupIdx: number) => void;
  // Trade condition handlers
  onUpdateTradeCondition: (id: string, idx: number, field: keyof TradeCondition, value: any) => void;
  // Exit signal handlers
  onAddExitSignal: (legId: string, groupIdx: number) => void;
  onRemoveExitSignal: (legId: string, groupIdx: number, signalIdx: number) => void;
  onUpdateExitSignal: (legId: string, groupIdx: number, signalIdx: number, signal: ConfiguredSignal) => void;
  onAddExitGroup: (legId: string) => void;
  onRemoveExitGroup: (legId: string, groupIdx: number) => void;
  legIndex: number;
  intradayEnabled?: boolean;
  allowedSignalTypes?: string[];
  entryAllowedSignalTypes?: string[];
  exitAllowedSignalTypes?: string[];
  copiedSignal: CopiedSignalPayload | null;
  onCopySignal: (signal: CopiedSignalPayload) => void;
  copiedLeg: CopiedLegPayload | null;
  onCopyLeg: (leg: Leg) => void;
  onPasteLeg: (legId: string) => void;
}


export const LegCard: React.FC<LegCardProps> = ({
  leg,
  symbolBank,
  onRemoveLeg,
  onAddEntrySignal,
  onRemoveEntrySignal,
  onUpdateEntrySignal,
  onAddEntryGroup,
  onRemoveEntryGroup,
  onUpdateTradeCondition,
  onAddExitSignal,
  onRemoveExitSignal,
  onUpdateExitSignal,
  onAddExitGroup,
  onRemoveExitGroup,
  legIndex,
  intradayEnabled = false,
  allowedSignalTypes,
  entryAllowedSignalTypes,
  exitAllowedSignalTypes,
  copiedSignal,
  onCopySignal,
  copiedLeg,
  onCopyLeg,
  onPasteLeg,
}) => {
  const entryAllowed = entryAllowedSignalTypes ?? allowedSignalTypes;
  const exitAllowed = exitAllowedSignalTypes ?? allowedSignalTypes;
  const hasGlobalSymbols = symbolBank.some(s => s.trim() !== "");
  const canPasteLeg = copiedLeg !== null;

  return (
    <div className={`${baseCard} ${styles.card}`}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={`${sectionTitle} ${styles.legTitle}`}>
          {leg.name}
        </h3>
        <div className={styles.headerActions}>
          <button
            className={styles.actionButton}
            onClick={() => onCopyLeg(leg)}
            aria-label="Copy leg"
            title="Copy signals, parameters, and trade conditions from this leg"
          >
            <Copy />
          </button>
          <button
            className={`${styles.actionButton} ${styles.pasteButton}`}
            onClick={() => onPasteLeg(leg.id)}
            aria-label="Paste leg"
            title={canPasteLeg ? "Paste copied leg into this leg" : "No copied leg to paste"}
            disabled={!canPasteLeg}
          >
            <ClipboardPaste />
          </button>
          <button
            className={styles.removeButton}
            onClick={() => onRemoveLeg(leg.id)}
            aria-label="Remove leg"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
      </div>

      {/* Three-column layout */}
      <div className={styles.threeColumnLayout}>
        {/* Entry Rules */}
        <div className={styles.section}>
          <OrGroupEditor
            groups={leg.entryRules}
            legId={leg.id}
            label="Entry"
            category="entry"
            onAddSignal={onAddEntrySignal}
            onRemoveSignal={onRemoveEntrySignal}
            onUpdateSignal={onUpdateEntrySignal}
            onAddGroup={onAddEntryGroup}
            onRemoveGroup={onRemoveEntryGroup}
            allowedSignalTypes={entryAllowed}
            copiedSignal={copiedSignal}
            onCopySignal={onCopySignal}
          />
        </div>

        {/* Exit Rules */}
        <div className={styles.section}>
          <OrGroupEditor
            groups={leg.exitRules}
            legId={leg.id}
            label="Exit"
            category="exit"
            onAddSignal={onAddExitSignal}
            onRemoveSignal={onRemoveExitSignal}
            onUpdateSignal={onUpdateExitSignal}
            onAddGroup={onAddExitGroup}
            onRemoveGroup={onRemoveExitGroup}
            allowedSignalTypes={exitAllowed}
            copiedSignal={copiedSignal}
            onCopySignal={onCopySignal}
          />
        </div>

        {/* Trade Conditions - always show, but symbol fields are conditional */}
        <div className={styles.section}>
          <TradeConditions
            tradeConditions={leg.tradeConditions}
            onUpdateCondition={(idx, field, value) => onUpdateTradeCondition(leg.id, idx, field, value)}
            legIndex={legIndex}
            hasGlobalSymbols={hasGlobalSymbols}
            intradayEnabled={intradayEnabled}
          />
        </div>
      </div>
    </div>
  );
};
